import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cake, Save, Gift, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { RoleSelect } from "@/components/RoleSelect";

const field = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";
const lbl = "text-xs uppercase tracking-wider text-zinc-500";
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TIMEZONES = ["UTC", "Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Chicago",
  "America/Denver", "America/Los_Angeles", "America/Sao_Paulo", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore",
  "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland"];

export default function Birthday() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get(`/guilds/${guildId}/modules/birthday`).then((r) => setCfg(r.data));
    api.get(`/guilds/${guildId}/birthdays`).then((r) => setList(r.data)).catch(() => {});
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
  }, [guildId]);

  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const set = (k, v) => setCfg((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));
  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/birthday`, { enabled: cfg.enabled, config: cfg.config });
    toast.success("Birthday settings saved");
  };
  const testAnnounce = async () => {
    setTesting(true);
    try {
      await api.post(`/guilds/${guildId}/birthday/test`);
      toast.success("Test announcement queued — check your channel in a few seconds 🎂");
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  return (
    <div>
      <PageHead title="Birthdays" desc="Celebrate members automatically with a message and a special role.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Module</span><Toggle testid="birthday-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="birthday-test" onClick={testAnnounce} disabled={testing || !cfg.enabled} title={cfg.enabled ? "Post a sample birthday announcement now" : "Enable & save the module first"}
            className="border border-volt/40 text-volt font-semibold rounded-md px-4 py-2 hover:bg-volt/10 disabled:opacity-40 flex items-center gap-2"><Send className="w-4 h-4" /> Test announce</button>
          <button data-testid="birthday-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><Cake className="w-4 h-4 text-volt" /> Announcement</h3>
          <div><label className={lbl}>Announcement channel</label>
            <ChannelSelect guildId={guildId} value={c.channel} onChange={(v) => set("channel", v)} testid="birthday-channel" placeholder="— select a channel —" /></div>
          <div><label className={lbl}>Birthday role (optional)</label>
            <RoleSelect guildId={guildId} value={c.role} onChange={(v) => set("role", v)} testid="birthday-role" placeholder="— select a role (optional) —" className="mt-1 w-full" />
            <p className="text-xs text-zinc-600 mt-1">Assigned for the day, then removed automatically.</p></div>
          <div><label className={lbl}>Message · use {"{user}"}</label>
            <textarea data-testid="birthday-message" value={c.message || ""} onChange={(e) => set("message", e.target.value)} rows={2} className={field} /></div>
          <div><label className={lbl}>Ping role (optional)</label>
            <select data-testid="birthday-ping" value={c.ping_role || ""} onChange={(e) => set("ping_role", e.target.value)} className={field}>
              <option value="">— no ping —</option>
              <option value="everyone">@everyone</option>
              <option value="here">@here</option>
              {roles.map((r) => <option key={r.id} value={r.id}>@{r.name}</option>)}
            </select>
            <p className="text-xs text-zinc-600 mt-1">Mentioned in the message so members get notified of the birthday.</p></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Announce hour (0–23)</label>
              <input data-testid="birthday-hour" type="number" min={0} max={23} value={c.announce_hour ?? 9} onChange={(e) => set("announce_hour", Math.max(0, Math.min(23, parseInt(e.target.value || 0, 10))))} className={field} /></div>
            <div><label className={lbl}>Timezone</label>
              <select data-testid="birthday-timezone" value={c.timezone || "UTC"} onChange={(e) => set("timezone", e.target.value)} className={field}>
                {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
          </div>
          <div className="flex items-start gap-2 bg-volt/5 border border-volt/20 rounded-lg p-3">
            <Gift className="w-4 h-4 text-volt mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400">Members add their own birthday in Discord with <code className="text-volt">/birthday set</code>. The bot announces at the hour &amp; timezone you choose.</p>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold flex items-center gap-2 mb-4"><Cake className="w-4 h-4 text-volt" /> Registered birthdays ({list.length})</h3>
          {list.length === 0 ? <p className="text-sm text-zinc-600">No birthdays yet. Members register with <code className="text-volt">/birthday set</code> in your server.</p> : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {list.map((b, i) => (
                <div key={i} data-testid={`birthday-row-${i}`} className="flex items-center justify-between bg-black border border-zinc-800 rounded-lg px-4 py-2 text-sm">
                  <span>{b.username}</span>
                  <span className="text-volt font-medium">🎂 {MONTHS[b.month]} {b.day}{b.year ? `, ${b.year}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
